import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  MenuItem,
  Select,
  Divider,
} from "@material-ui/core";
import { commonInlineStyles, inputFieldStyles } from "../../styles";

const POConfigurationsSection = forwardRef(
  (
    {
      form: initialForm,
      errors: initialErrors,
      editMode: initialEditMode = false,
      onChange,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      disableEdit = false,
    },
    ref
  ) => {
    const [form, setForm] = useState({ ...initialForm });
    const [errors, setErrors] = useState({ ...initialErrors });
    const [editMode, setEditMode] = useState(initialEditMode);
    const [isModified, setIsModified] = useState(false);
    const [originalForm, setOriginalForm] = useState({ ...initialForm });

    // Update state when props change
    useEffect(() => {
      const formCopy = { ...initialForm };
      setForm(formCopy);
      setOriginalForm({ ...formCopy });
      setErrors({ ...initialErrors });
      setEditMode(initialEditMode);
      setIsModified(false);
    }, [initialForm, initialErrors, initialEditMode]);

    // Check if form has been modified 
    useEffect(() => {
      const hasChanged = JSON.stringify(form) !== JSON.stringify(originalForm);
      if (isModified !== hasChanged) {
        setIsModified(hasChanged);
        if (onChange) {
          onChange({ target: { name: "formChanged", value: hasChanged } });
        }
      }
    }, [form, originalForm, onChange, isModified]);

    const handleFieldChange = (e) => {
      const { name, value } = e.target;
      
      // For acceptance limit fields, only allow numbers
      if (
        name === "acceptanceLimitEarlyDelivery" ||
        name === "acceptanceLimitLaterDelivery" ||
        name === "acceptanceLimitLessQuantity" ||
        name === "acceptanceLimitMoreQuantity"
      ) {
        // Only allow numbers, set isModified based on validity
        if (value === "" || /^[0-9]+$/.test(value)) {
          const newForm = { ...form, [name]: value };
          setForm(newForm);
          setIsModified(true);

          if (onChange) {
            onChange({ target: { name, value } });
          }
        } else {
          // Invalid input, don't update form but set isModified to false
          setIsModified(false);
        }
      } else {
        // For other fields, normal behavior
        const newForm = { ...form, [name]: value };
        setForm(newForm);
        setIsModified(true);

        if (onChange) {
          onChange({ target: { name, value } });
        }
      }

      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    };

    const validateForm = () => {
      const newErrors = {};
      let isValid = true;

      if (!form.cmoUsePOInterface) {
        newErrors.cmoUsePOInterface = "This field is required.";
        isValid = false;
      }

      // For number fields, allow "N/A" as valid
      if (
        !form.acceptanceLimitEarlyDelivery ||
        String(form.acceptanceLimitEarlyDelivery).trim() === ""
      ) {
        newErrors.acceptanceLimitEarlyDelivery = "This field is required.";
        isValid = false;
      } else if (
        form.acceptanceLimitEarlyDelivery !== "N/A" &&
        !/^[0-9]+$/.test(String(form.acceptanceLimitEarlyDelivery))
      ) {
        newErrors.acceptanceLimitEarlyDelivery =
          "Please enter a valid number or N/A.";
        isValid = false;
      }

      if (
        !form.acceptanceLimitLaterDelivery ||
        String(form.acceptanceLimitLaterDelivery).trim() === ""
      ) {
        newErrors.acceptanceLimitLaterDelivery = "This field is required.";
        isValid = false;
      } else if (
        form.acceptanceLimitLaterDelivery !== "N/A" &&
        !/^[0-9]+$/.test(String(form.acceptanceLimitLaterDelivery))
      ) {
        newErrors.acceptanceLimitLaterDelivery =
          "Please enter a valid number or N/A.";
        isValid = false;
      }

      if (
        !form.acceptanceLimitLessQuantity ||
        String(form.acceptanceLimitLessQuantity).trim() === ""
      ) {
        newErrors.acceptanceLimitLessQuantity = "This field is required.";
        isValid = false;
      } else if (
        form.acceptanceLimitLessQuantity !== "N/A" &&
        !/^[0-9]+$/.test(String(form.acceptanceLimitLessQuantity))
      ) {
        newErrors.acceptanceLimitLessQuantity =
          "Please enter a valid number or N/A.";
        isValid = false;
      }

      if (
        !form.acceptanceLimitMoreQuantity ||
        String(form.acceptanceLimitMoreQuantity).trim() === ""
      ) {
        newErrors.acceptanceLimitMoreQuantity = "This field is required.";
        isValid = false;
      } else if (
        form.acceptanceLimitMoreQuantity !== "N/A" &&
        !/^[0-9]+$/.test(String(form.acceptanceLimitMoreQuantity))
      ) {
        newErrors.acceptanceLimitMoreQuantity =
          "Please enter a valid number or N/A.";
        isValid = false;
      }

      setErrors(newErrors);
      return isValid;
    };

    const getFormData = () => form;

    const getPayload = () => ({
      updatePOConfigFlag: true,
      vendorPlantDetails: {
        cmoToUsePOInterface: form.cmoUsePOInterface === "Yes",

        acceptanceLimitForEarlyDeliveryDateChangeByCMO:
          form.acceptanceLimitEarlyDelivery === "N/A"
            ? null
            : parseInt(form.acceptanceLimitEarlyDelivery),
        acceptanceLimitForLaterDeliveryDateChangeByCMO:
          form.acceptanceLimitLaterDelivery === "N/A"
            ? null
            : parseInt(form.acceptanceLimitLaterDelivery),
        acceptanceLimitForLessDeliveryQuantityChangeByCMO:
          form.acceptanceLimitLessQuantity === "N/A"
            ? null
            : parseInt(form.acceptanceLimitLessQuantity),
        acceptanceLimitForMoreDeliveryQuantityChangeByCMO:
          form.acceptanceLimitMoreQuantity === "N/A"
            ? null
            : parseInt(form.acceptanceLimitMoreQuantity),
      },
    });

    const resetForm = () => {
      setForm({ ...originalForm });
      setErrors({});
      setIsModified(false);
    };

    useImperativeHandle(ref, () => ({
      validateForm,
      getFormData,
      getPayload,
      resetForm,
    }));

    return (
      <div className="cmo-stepper-wrapper shipment-config-step">
        <div className="stepper-container">
          <form>
            <Grid className="two-col-forms-grid" container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography component="label" htmlFor="cmoUsePOInterface">
                    CMO to use PO Interface{" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.cmoUsePOInterface || ""}</Typography>
                  ) : (
                    <Select
                      select
                      id="cmoUsePOInterface"
                      name="cmoUsePOInterface"
                      value={form.cmoUsePOInterface || ""}
                      onChange={handleFieldChange}
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.cmoUsePOInterface}
                      helperText={errors.cmoUsePOInterface || ""}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography
                    component="label"
                    htmlFor="acceptanceLimitEarlyDelivery"
                  >
                    Acceptance limit for (early) delivery date change by CMO (in
                    days) {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>
                      {form.acceptanceLimitEarlyDelivery || ""}
                    </Typography>
                  ) : (
                    <TextField
                      id="acceptanceLimitEarlyDelivery"
                      name="acceptanceLimitEarlyDelivery"
                      value={
                        form.acceptanceLimitEarlyDelivery !== undefined &&
                        form.acceptanceLimitEarlyDelivery !== null
                          ? form.acceptanceLimitEarlyDelivery
                          : ""
                      }
                      onChange={handleFieldChange}
                      variant="outlined"
                      placeholder="Enter number of days"
                      fullWidth
                      required
                      error={!!errors.acceptanceLimitEarlyDelivery}
                      helperText={errors.acceptanceLimitEarlyDelivery || ""}
                      InputProps={{ style: commonInlineStyles.input }}
                      sx={inputFieldStyles}
                      inputProps={{ ...commonInlineStyles.inputProps }}
                      style={commonInlineStyles.input}
                      // onInput={(e) => {
                      //   e.target.value = e.target.value.replace(/[^0-9]/g, "");
                      // }}
                    />
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography
                    component="label"
                    htmlFor="acceptanceLimitLaterDelivery"
                  >
                    Acceptance limit for (later) delivery date change by CMO (in
                    days) {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>
                      {form.acceptanceLimitLaterDelivery || ""}
                    </Typography>
                  ) : (
                    <TextField
                      id="acceptanceLimitLaterDelivery"
                      name="acceptanceLimitLaterDelivery"
                      value={
                        form.acceptanceLimitLaterDelivery !== undefined &&
                        form.acceptanceLimitLaterDelivery !== null
                          ? form.acceptanceLimitLaterDelivery
                          : ""
                      }
                      onChange={handleFieldChange}
                      variant="outlined"
                      placeholder="Enter number of days"
                      fullWidth
                      required
                      error={!!errors.acceptanceLimitLaterDelivery}
                      helperText={errors.acceptanceLimitLaterDelivery || ""}
                      InputProps={{ style: commonInlineStyles.input }}
                      sx={inputFieldStyles}
                      inputProps={{ ...commonInlineStyles.inputProps }}
                      style={commonInlineStyles.input}
                      // onInput={(e) => {
                      //   e.target.value = e.target.value.replace(/[^0-9]/g, "");
                      // }}
                    />
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography
                    component="label"
                    htmlFor="acceptanceLimitLessQuantity"
                  >
                    Acceptance limit for (less) delivery quantity change by CMO
                    (%)
                    {editMode && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>
                      {form.acceptanceLimitLessQuantity || ""}
                    </Typography>
                  ) : (
                    <TextField
                      id="acceptanceLimitLessQuantity"
                      name="acceptanceLimitLessQuantity"
                      value={
                        form.acceptanceLimitLessQuantity !== undefined &&
                        form.acceptanceLimitLessQuantity !== null
                          ? form.acceptanceLimitLessQuantity
                          : ""
                      }
                      onChange={handleFieldChange}
                      variant="outlined"
                      placeholder="Enter percentage"
                      fullWidth
                      required
                      error={!!errors.acceptanceLimitLessQuantity}
                      helperText={errors.acceptanceLimitLessQuantity || ""}
                      InputProps={{ style: commonInlineStyles.input }}
                      sx={inputFieldStyles}
                      inputProps={{ ...commonInlineStyles.inputProps }}
                      style={commonInlineStyles.input}
                    />
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography
                    component="label"
                    htmlFor="acceptanceLimitMoreQuantity"
                  >
                    Acceptance limit for (more) delivery quantity change by CMO
                    (%)
                    {editMode && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>
                      {form.acceptanceLimitMoreQuantity || ""}
                    </Typography>
                  ) : (
                    <TextField
                      id="acceptanceLimitMoreQuantity"
                      name="acceptanceLimitMoreQuantity"
                      value={
                        form.acceptanceLimitMoreQuantity !== undefined &&
                        form.acceptanceLimitMoreQuantity !== null
                          ? form.acceptanceLimitMoreQuantity
                          : ""
                      }
                      onChange={handleFieldChange}
                      variant="outlined"
                      placeholder="Enter percentage"
                      fullWidth
                      required
                      error={!!errors.acceptanceLimitMoreQuantity}
                      helperText={errors.acceptanceLimitMoreQuantity || ""}
                      InputProps={{ style: commonInlineStyles.input }}
                      sx={inputFieldStyles}
                      inputProps={{ ...commonInlineStyles.inputProps }}
                      style={commonInlineStyles.input}
                    />
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </form>
          <Divider
            style={{
              margin: "80px 00px 0px 00px",
              backgroundColor: "#707070",
              height: 1,
            }}
          />
        </div>
      </div>
    );
  }
);

export default POConfigurationsSection;
